// Detects new deployments while a tab stays open and triggers a safe refresh.
// The build version is injected at build time as VITE_SENTRY_RELEASE (manual-<sha>-<env>)
// and published as <base>version.json (no-cache). We poll it and compare against the running bundle.
// Shared by the portal (base `/`) and the admin panel (base `/a9f3k2-admin-panel/`), so the
// manifest is resolved against the bundle base — a hardcoded `/version.json` would 404 in admin.

const resolveBase = () => {
  const base = String(import.meta.env.BASE_URL || '/')
  return base.endsWith('/') ? base : `${base}/`
}

const resolveVersionUrl = () => `${resolveBase()}version.json`
const POLL_INTERVAL_MS = 5 * 60 * 1000
const RELOAD_GUARD_KEY = 'cargonice-version-reloaded'

let watcherInstalled = false
let pendingVersion: string | null = null
const listeners = new Set<() => void>()

export const getCurrentVersion = (): string => String(import.meta.env.VITE_SENTRY_RELEASE || '').trim()

export const isUpdateAvailable = (): boolean => Boolean(pendingVersion)

export const subscribeVersionUpdate = (listener: () => void): (() => void) => {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

const notify = () => {
  listeners.forEach((listener) => {
    try {
      listener()
    } catch {
      // Ignore listener errors.
    }
  })
}

// Portal and admin live on the same origin (and therefore share sessionStorage), and both
// ship the same `manual-<sha>-prod` release string — the guard is scoped by base path so a
// reload done by one app does not swallow the other's.
const reloadGuardValue = (version: string) => `${resolveBase()}|${version}`

const alreadyReloadedFor = (version: string): boolean => {
  try {
    return window.sessionStorage.getItem(RELOAD_GUARD_KEY) === reloadGuardValue(version)
  } catch {
    return false
  }
}

const markReloadedFor = (version: string) => {
  try {
    window.sessionStorage.setItem(RELOAD_GUARD_KEY, reloadGuardValue(version))
  } catch {
    // Ignore storage errors.
  }
}

// Guarded auto-reload (used on in-app navigation). Reloads at most once per detected version
// so a rollout still serving the old bundle on some pods cannot trigger a reload loop.
export const reloadForUpdate = (reload: () => void = () => window.location.reload()): boolean => {
  if (!pendingVersion || typeof window === 'undefined') {
    return false
  }
  if (alreadyReloadedFor(pendingVersion)) {
    return false
  }
  markReloadedFor(pendingVersion)
  reload()
  return true
}

const fetchDeployedVersion = async (): Promise<string> => {
  try {
    const response = await fetch(resolveVersionUrl(), { cache: 'no-store' })
    if (!response.ok) {
      return ''
    }
    const data = (await response.json()) as { version?: unknown }
    return String(data?.version || '').trim()
  } catch {
    return ''
  }
}

const checkForUpdate = async (): Promise<void> => {
  const current = getCurrentVersion()
  if (!current) {
    return // Dev / unknown release — nothing to compare against.
  }
  const deployed = await fetchDeployedVersion()
  if (deployed && deployed !== current && deployed !== pendingVersion) {
    pendingVersion = deployed
    notify()
  }
}

export const installVersionWatcher = (): void => {
  if (watcherInstalled || typeof window === 'undefined') {
    return
  }
  watcherInstalled = true
  void checkForUpdate()
  window.setInterval(() => {
    void checkForUpdate()
  }, POLL_INTERVAL_MS)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      void checkForUpdate()
    }
  })
  window.addEventListener('focus', () => {
    void checkForUpdate()
  })
}
